import { ref, computed, watch, onMounted, onUnmounted, inject } from 'vue'
import type { Ref } from 'vue'
import type {
  FieldEntityTarget,
  FieldOrigin,
  CustomField,
  FieldConfig,
  ResolvedField,
  SystemFieldDefinition
} from '@gat-ship/types'
import type {
  AddCustomFieldRequestPayload,
  UpdateCustomFieldRequestPayload,
  SetFieldConfigRequestPayload
} from '@gat-ship/types'
import type { CustomFieldsAdminService } from '@gat-ship/types/injection-keys'
import { customFieldsAdminKey } from '@gat-ship/types/injection-keys'
import { useFieldConfigurationApiCalls } from './field-configuration-api-calls'
import { getSystemFields } from '../model/define-system-fields'
import { resolveAdminFields } from '../model/resolve-admin-fields'
import { useFieldConfigurationSignalREvents } from '@gat-ship/signalr'
import { useDebounceFn } from '@vueuse/core'
import { globalUtils } from '@gat-ship/utils'

/**
 * Composable for the custom fields admin UI.
 * Manages field definitions, configurations, and CRUD operations.
 */
export function useCustomFieldsAdmin(
  target: Ref<FieldEntityTarget>
): CustomFieldsAdminService {
  const loading = ref(false)
  const customFields = ref<CustomField[]>([])
  const fieldConfigs = ref<FieldConfig[]>([])

  const {
    getCustomFieldsRequest,
    getFieldConfigurationsRequest,
    addCustomFieldRequest,
    updateCustomFieldRequest,
    deleteCustomFieldRequest,
    setCustomFieldsOrderRequest,
    setFieldConfigRequest,
    resetFieldConfigRequest
  } = useFieldConfigurationApiCalls()

  // ── System Fields ─────────────────────────────────────────────

  const systemFields = computed<SystemFieldDefinition[]>(() =>
    getSystemFields(target.value)
  )

  // ── Resolved Fields ───────────────────────────────────────────

  const resolvedFields = computed<ResolvedField[]>(() =>
    resolveAdminFields(
      systemFields.value,
      customFields.value,
      fieldConfigs.value
    )
  )

  // ── Data Loading ──────────────────────────────────────────────

  async function load() {
    loading.value = true
    try {
      const [cfRes, fcRes] = await Promise.all([
        getCustomFieldsRequest(target.value),
        getFieldConfigurationsRequest(target.value)
      ])
      customFields.value = cfRes.fields
      fieldConfigs.value = fcRes.configurations
    } finally {
      loading.value = false
    }
  }

  load()
  watch(target, load)

  // ── SignalR Live Reload ────────────────────────────────────────

  const debouncedLoad = useDebounceFn(load, 300)

  const fieldConfigSignalR = useFieldConfigurationSignalREvents({
    onFieldConfigChanged: (payload) => {
      if (payload.message.entityTarget !== target.value) return
      debouncedLoad()
    },
    onCustomFieldAdded: () => {
      debouncedLoad()
    },
    onCustomFieldUpdated: () => {
      debouncedLoad()
    },
    onCustomFieldRemoved: () => {
      debouncedLoad()
    },
    onCustomFieldsOrderChanged: (payload) => {
      if (payload.message.entityTarget !== target.value) return
      debouncedLoad()
    }
  })

  onMounted(() => {
    fieldConfigSignalR.subscribeToFieldConfigurationEvents()
  })
  onUnmounted(() => {
    fieldConfigSignalR.unsubscribeFromFieldConfigurationEvents()
  })

  // ── Custom Field CRUD ─────────────────────────────────────────

  const loadingFieldIds = ref(new Set<string>())

  async function addCustomField(payload: AddCustomFieldRequestPayload) {
    const tempField: CustomField = {
      ...payload,
      sortOrder: customFields.value.length
    }
    customFields.value.push(tempField)
    loadingFieldIds.value = new Set([...loadingFieldIds.value, payload.id])

    try {
      const commandId = globalUtils.functions.uuid()
      const result = await addCustomFieldRequest(payload, commandId)
      const index = customFields.value.findIndex((f) => f.id === payload.id)
      if (index !== -1) customFields.value[index] = result.field
    } catch (err) {
      customFields.value = customFields.value.filter((f) => f.id !== payload.id)
      throw err
    } finally {
      const next = new Set(loadingFieldIds.value)
      next.delete(payload.id)
      loadingFieldIds.value = next
    }
  }

  async function updateCustomField(
    id: string,
    payload: UpdateCustomFieldRequestPayload
  ) {
    const commandId = globalUtils.functions.uuid()
    const result = await updateCustomFieldRequest(id, payload, commandId)
    const index = customFields.value.findIndex((f) => f.id === id)
    if (index !== -1 && customFields.value[index]) {
      customFields.value[index] = {
        ...result.field,
        sortOrder: customFields.value[index].sortOrder
      }
    }
  }

  async function deleteCustomField(id: string) {
    const commandId = globalUtils.functions.uuid()
    await deleteCustomFieldRequest(id, commandId)
    customFields.value = customFields.value.filter((f) => f.id !== id)
  }

  async function reorderCustomFields(orderedIds: string[]) {
    const commandId = globalUtils.functions.uuid()
    await setCustomFieldsOrderRequest(target.value, { orderedIds }, commandId)
    orderedIds.forEach((id, i) => {
      const field = customFields.value.find((f) => f.id === id)
      if (field) field.sortOrder = i
    })
  }

  // ── Field Config ──────────────────────────────────────────────

  async function setFieldConfig(
    origin: FieldOrigin,
    fieldName: string,
    payload: SetFieldConfigRequestPayload
  ) {
    const commandId = globalUtils.functions.uuid()
    const result = await setFieldConfigRequest(
      target.value,
      origin,
      fieldName,
      payload,
      commandId
    )
    // Update local state with new config
    const newConfig: FieldConfig = {
      fieldName,
      origin,
      entityTarget: target.value,
      hint: result.newConfig.hint,
      requiredOption: result.newConfig.requiredOption,
      visibilityOption: result.newConfig.visibilityOption,
      visibleForRoleIds: result.newConfig.visibleForRoleIds,
      editableOption: result.newConfig.editableOption,
      editableForRoleIds: result.newConfig.editableForRoleIds
    }
    const index = fieldConfigs.value.findIndex(
      (c) => c.origin === origin && c.fieldName === fieldName
    )
    if (index !== -1) {
      fieldConfigs.value[index] = newConfig
    } else {
      fieldConfigs.value.push(newConfig)
    }
  }

  async function resetFieldConfig(origin: FieldOrigin, fieldName: string) {
    const commandId = globalUtils.functions.uuid()
    await resetFieldConfigRequest(target.value, origin, fieldName, commandId)
    fieldConfigs.value = fieldConfigs.value.filter(
      (c) => !(c.origin === origin && c.fieldName === fieldName)
    )
  }

  return {
    loading,
    loadingFieldIds,
    systemFields,
    customFields,
    fieldConfigs,
    resolvedFields,
    load,
    addCustomField,
    updateCustomField,
    deleteCustomField,
    reorderCustomFields,
    setFieldConfig,
    resetFieldConfig
  }
}

/** Resolve a provided admin datasource from context, if any. */
export function useCustomFieldsAdminContext() {
  return inject(customFieldsAdminKey, undefined)
}
